"use strict";

const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

class DynamicStylesheetUtility extends EventEmitter {
    constructor() {
        super();

        this._stylesheetTitle = "dynamic_stylesheet";
        this._dynamicStylesheet = undefined;
        this._styleRules = undefined;

        for (let i = 0; i < document.styleSheets.length; i++) {
            if (document.styleSheets[i].title === this._stylesheetTitle) {
                this._dynamicStylesheet = document.styleSheets[i];
                this._styleRules = this._dynamicStylesheet.cssRules;
            }
        }

        if (_.isUndefined(this._dynamicStylesheet)) {
            let style = document.createElement("style");
            style.appendChild(document.createTextNode(""));
            style.title = this._stylesheetTitle;
            document.head.appendChild(style);
            this._dynamicStylesheet = style.sheet;
            this._styleRules = this._dynamicStylesheet.cssRules;
        }

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._stylesheetTitle = undefined;
        this._dynamicStylesheet = undefined;
        this._styleRules = undefined;
        return Promise.resolve();
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /**
     * Create a new CSSRule with the passed in ruleConfig style property
     * @param ruleConfig.selector {String} - The CSS selector used to create the style rule
     * @param ruleConfig.property {String} - The CSS property to which the style rule should be applied
     * @param ruleConfig.value {String} - The CSS value for the property in the style rule
     * @private
     */
    _appendStyleRule(ruleConfig) {
        this._dynamicStylesheet.insertRule(
            `${ruleConfig.selector} { ${ruleConfig.property}: ${ruleConfig.value}; }`,
            this._styleRules.length
        );
    }

    /**
     * Keep any existing property/values for the existing rule but override the value for the passed in ruleConfig style property
     * @param ruleConfig.selector {String} - The CSS selector used to create the style rule
     * @param ruleConfig.property {String} - The CSS property to which the style rule should be applied
     * @param ruleConfig.value {String} - The CSS value for the property in the style rule
     * @param index {Number} - Position in the rules array of the style rule to be overridden
     * @param existingRule {CSSRule} - Existing style rule
     * @private
     */
    _overrideStyleRule(ruleConfig, index, existingRule) {
        let allStyleProperties = existingRule.style.cssText.split(";"),
            newStyleProperties = "";
        allStyleProperties.forEach((propertyAndValue) => {
            let property = propertyAndValue.substring(0, propertyAndValue.indexOf(":")).trim();
            if (property.length > 0) {
                if (property === ruleConfig.property) {
                    newStyleProperties += `${ruleConfig.property}: ${ruleConfig.value}; `;
                } else {
                    newStyleProperties += `${propertyAndValue.trim()}; `;
                }
            }
        });
        this._dynamicStylesheet.deleteRule(index);
        this._dynamicStylesheet.insertRule(
            `${ruleConfig.selector} { ${newStyleProperties} }`,
            this._styleRules.length
        );
    }

    /**
     * Insert the passed in property/value into the existing rule
     * @param ruleConfig.selector {String} - The CSS selector used to create the style rule
     * @param ruleConfig.property {String} - The CSS property to which the style rule should be applied
     * @param ruleConfig.value {String} - The CSS value for the property in the style rule
     * @param index {Number} - Position in the rules array of the style rule to be overridden
     * @param existingRule {CSSRule} - Existing style rule
     * @private
     */
    _insertIntoStyleRule(ruleConfig, index, existingRule) {
        let newStyleProperty = ruleConfig.property + ": " + ruleConfig.value + ";",
            allStyleProperties = existingRule.style.cssText;
        allStyleProperties += " " + newStyleProperty;
        this._dynamicStylesheet.deleteRule(index);
        this._dynamicStylesheet.insertRule(
            ruleConfig.selector + " { " + allStyleProperties + " }",
            this._styleRules.length
        );
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * Returns the value of the passed in selector and property
     * @param selector {String} - The CSS selector used to create the style rule
     * @param styleProperty {String} - The CSS property for which the value will be returned
     * @returns {String}
     */
    getStyleValue(selector, styleProperty) {
        let styleValue;
        for (let i = 0; i < this._styleRules.length; i++) {
            if (this._styleRules[i].selectorText === selector) {
                let allStyleProperties = this._styleRules[i].style.cssText.split(";");
                for (let j = 0; j < allStyleProperties.length; j++) {
                    let propertyAndValue = allStyleProperties[j];
                    let property = propertyAndValue
                        .substring(0, propertyAndValue.indexOf(":"))
                        .trim();
                    let value = propertyAndValue
                        .substring(propertyAndValue.indexOf(":") + 1, propertyAndValue.length)
                        .trim();
                    if (property === styleProperty) {
                        styleValue = value;
                    }
                }
            }
        }
        return styleValue;
    }

    /**
     * Creates a style rule in the stylesheet using the passed in selector, property, and value
     * @param selector {String} - CSS Selector used to create the style rule
     * @param styleProperty {string} - CSS Property used to create the style rule
     * @param value {String} - CSS value for the property
     */
    setStyleValue(selector, styleProperty, value) {
        let foundIndex = -1,
            foundStyleProperty = false;
        for (let i = 0; i < this._styleRules.length; i++) {
            if (this._styleRules[i].selectorText === selector) {
                foundIndex = i;
                let allStyleProperties = this._styleRules[i].style.cssText.split(";");
                for (let j = 0; j < allStyleProperties.length; j++) {
                    let propertyAndValue = allStyleProperties[j];
                    let property = propertyAndValue
                        .substring(0, propertyAndValue.indexOf(":"))
                        .trim();
                    if (property === styleProperty) {
                        foundStyleProperty = true;
                        this._overrideStyleRule(
                            {
                                selector: selector,
                                property: styleProperty,
                                value: value,
                            },
                            foundIndex,
                            this._styleRules[foundIndex]
                        );
                    }
                }
                if (foundStyleProperty === false) {
                    this._insertIntoStyleRule(
                        {
                            selector: selector,
                            property: styleProperty,
                            value: value,
                        },
                        foundIndex,
                        this._styleRules[foundIndex]
                    );
                }
            }
        }
        if (foundIndex === -1) {
            this._appendStyleRule({
                selector: selector,
                property: styleProperty,
                value: value,
            });
        }
    }

    /**
     * Returns the dynamic stylesheet
     * @returns {CSSStyleSheet}
     */
    getDynamicStylesheet() {
        return this._dynamicStylesheet;
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = DynamicStylesheetUtility;
